<template>
  <div class="person-card">
    <div class="person-header">
      <h3 class="person-name"><router-link :to="`/people/${person.id}`" class="person-link">{{ person.name }}</router-link></h3>
      <span class="type-badge">{{ person.type }}</span>
    </div>

    <router-link v-if="pendingProposals.length" :to="pendingLink" class="gift-alert">
      🎁 {{ pendingProposals.length === 1
        ? 'Gift idea ready to review'
        : `${pendingProposals.length} gift ideas ready` }}
      <span class="gift-alert-cta">Review &rarr;</span>
    </router-link>

    <div class="vibe-tags" v-if="person.vibe && person.vibe.length">
      <span class="vibe-chip" v-for="v in person.vibe" :key="v">{{ v }}</span>
    </div>

    <div class="upcoming-date" v-if="upcomingDate">
      {{ upcomingDate.label }} {{ upcomingDateText }}
    </div>

    <div class="existing-dates" v-if="person.significantDates && person.significantDates.length">
      <div class="date-chip" v-for="(sd, idx) in person.significantDates" :key="idx">
        <span>{{ sd.label }} — {{ formatSignificantDate(sd) }}</span>
        <button v-if="showDateForm" class="remove-btn" @click="removeDate(idx)">&times;</button>
      </div>
    </div>

    <div class="saved-address" v-if="person.hasAddress && !showAddressForm">
      <span class="address-text">{{ formattedAddress }}</span>
    </div>

    <a class="add-date-link" href="#" @click.prevent="showAddressForm = !showAddressForm">
      {{ showAddressForm ? 'Cancel' : person.hasAddress ? 'Edit address' : '+ Add address' }}
    </a>

    <div v-if="showAddressForm" class="inline-address-form">
      <div class="field-group">
        <SimpleGoogleAutocomplete
          ref="addressAutocomplete"
          @update:modelValue="handleAddressUpdate"
        />
      </div>
      <div class="field-group">
        <input
          type="text"
          v-model="editAddress.optional"
          placeholder="Apt / Suite (optional)"
          class="date-input"
          style="width: 100%;"
        />
      </div>
      <div class="date-form-actions">
        <button
          class="button small primary"
          :disabled="!editAddress.street || !editAddress.city || !editAddress.postalCode || saving"
          @click="saveAddress"
        >
          Save
        </button>
        <button class="button small secondary" @click="showAddressForm = false">Cancel</button>
      </div>
    </div>

    <a class="add-date-link" href="#" @click.prevent="showDateForm = !showDateForm">
      {{ showDateForm ? 'Cancel' : hasDates ? 'Edit dates' : '+ Add date' }}
    </a>

    <div v-if="showDateForm" class="inline-date-form">
      <div class="date-add-row">
        <select v-model="newDateType" class="date-input date-select">
          <option value="">Type...</option>
          <option value="birthday">Birthday</option>
          <option value="anniversary">Anniversary</option>
          <option value="custom">Custom</option>
        </select>
        <input
          v-if="newDateType === 'custom'"
          type="text"
          v-model="newDateLabel"
          placeholder="Label"
          class="date-input date-label-input"
        />
        <select v-model="newDateMonth" class="date-input date-select">
          <option :value="0">Month...</option>
          <option v-for="m in 12" :key="m" :value="m">{{ monthName(m) }}</option>
        </select>
        <input
          type="number"
          v-model.number="newDateDay"
          placeholder="Day"
          min="1"
          max="31"
          class="date-input date-day-input"
        />
        <input
          type="number"
          v-model.number="newDateYear"
          placeholder="Year"
          min="1900"
          max="2100"
          class="date-input date-year-input"
        />
      </div>
      <div class="date-form-actions">
        <button class="button small primary" :disabled="!canAddDate || saving" @click="addDate">Save</button>
        <button class="button small secondary" @click="showDateForm = false">Cancel</button>
      </div>
    </div>

    <a class="add-date-link" href="#" :class="{ 'signal-nudge': signalsStale && !showSignalForm }" @click.prevent="showSignalForm = !showSignalForm">
      {{ showSignalForm ? 'Cancel' : signalsStale ? `+ Anything new with ${firstName}?` : '+ They mentioned something…' }}
    </a>

    <div v-if="showSignalForm" class="inline-date-form">
      <div class="field-group">
        <input
          type="text"
          v-model="newSignalText"
          maxlength="300"
          placeholder="e.g. broke her favorite mug, started trail running again"
          class="date-input"
          style="width: 100%;"
          @keyup.enter="addSignal"
        />
      </div>
      <p class="signal-hint">
        Fresh details like this are how your assistant finds the exact right
        gift — and can suggest one when it matters, not just on birthdays.
      </p>
      <div class="date-form-actions">
        <button class="button small primary" :disabled="!newSignalText.trim() || saving" @click="addSignal">Save</button>
        <button class="button small secondary" @click="showSignalForm = false">Cancel</button>
      </div>
    </div>

    <p class="stat latest-signal" v-if="latestSignal">
      💬 “{{ latestSignal.text }}” <span class="signal-when">({{ timeAgo(latestSignal.at) }})</span>
    </p>

    <div class="person-stats">
      <p class="stat" v-if="person.totalSent > 0">
        {{ person.totalSent }} postcard{{ person.totalSent === 1 ? '' : 's' }} sent
      </p>
      <p class="stat" v-else>No postcards sent yet</p>
      <p class="stat last-sent" v-if="person.lastContactAt">
        Last sent {{ timeAgo(person.lastContactAt) }}
      </p>
    </div>

    <div class="agent-status" :class="agentStatus.cls" :title="'Whether your assistant may reach out on its own'">
      <span class="agent-status-icon" aria-hidden="true">{{ agentStatus.icon }}</span>
      <span class="agent-status-label">{{ agentStatus.label }}</span>
    </div>

    <div class="person-actions">
      <button class="button primary" @click="$emit('send', person)">Send</button>
      <button class="button secondary" @click="$emit('edit', person)">Edit</button>
    </div>
  </div>
</template>

<script>
import { db } from "@/firebase";
import { updateDoc, doc } from "firebase/firestore";
import { AddressAutocomplete as SimpleGoogleAutocomplete } from "@thinking-of-u/ui";
import { monthName, formatSignificantDate } from "@thinking-of-u/shared";
import { isEmailContactable, isPhysicalContactable } from "@/utils/agentEligibility";

const FREQUENCY_LABELS = {
  weekly: "weekly",
  biweekly: "every 2 weeks",
  monthly: "monthly",
  quarterly: "every few months",
  whenever: "when it feels right",
};

export default {
  name: "PersonCard",
  components: { SimpleGoogleAutocomplete },
  props: {
    person: {
      type: Object,
      required: true,
    },
    pendingProposals: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["send", "edit", "updated"],
  data() {
    return {
      showDateForm: false,
      showAddressForm: false,
      showSignalForm: false,
      newSignalText: "",
      newDateType: "",
      newDateMonth: 0,
      newDateDay: null,
      newDateYear: null,
      newDateLabel: "",
      saving: false,
      editAddress: {
        street: "",
        optional: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
      },
    };
  },
  computed: {
    latestSignal() {
      const signals = this.person.signals || [];
      return signals.length ? signals[signals.length - 1] : null;
    },
    firstName() {
      return (this.person.name || '').split(' ')[0] || 'them';
    },
    // Nudge when the graph has gone quiet: no mention recorded in 30+ days
    // (or ever) — fresh signals are what make postcards and gifts land.
    signalsStale() {
      if (!this.latestSignal) return true;
      return Date.now() - (this.latestSignal.at || 0) > 30 * 86400000;
    },
    // One pending proposal links straight to its review page; multiple go to
    // the gifts inbox where they can be triaged.
    pendingLink() {
      if (this.pendingProposals.length === 1) {
        return `/gifts/${this.pendingProposals[0].id}`;
      }
      return "/gifts";
    },
    // Whether/how the assistant may auto-reach this person, mirroring the
    // orchestrator's consent gates so the sender can see (and fix) eligibility.
    agentStatus() {
      const p = this.person;
      const channel = p.preferredChannel || "off";
      const freq = FREQUENCY_LABELS[p.frequency];
      const suffix = freq ? ` · ${freq}` : "";
      // Readiness, not active behaviour: the agent is globally gated, so the
      // "on" state means "eligible to be reached", not "currently sending".
      if (channel === "email") {
        if (isEmailContactable(p)) return { cls: "on", icon: "✉️", label: `Ready to auto-send by email${suffix}` };
        const hasConsent = p.consent === true || !!p.consentedAt;
        return { cls: "warn", icon: "⚠️", label: hasConsent ? "Email set — add a valid email" : "Email set — needs consent" };
      }
      if (channel === "physical") {
        if (isPhysicalContactable(p)) return { cls: "on", icon: "📮", label: `Ready to auto-send by mail${suffix}` };
        const hasConsent = p.postalConsent === true || !!p.postalConsentedAt;
        return { cls: "warn", icon: "⚠️", label: hasConsent ? "Mail set — add an address" : "Mail set — needs consent" };
      }
      return { cls: "off", icon: "⏸", label: "Auto-send off" };
    },
    hasDates() {
      return this.person.significantDates && this.person.significantDates.length > 0;
    },
    formattedAddress() {
      const a = this.person.address;
      if (!a) return "";
      const parts = [a.street, a.optional, a.city, a.state, a.postalCode, a.country].filter(Boolean);
      return parts.join(", ");
    },
    canAddDate() {
      if (!this.newDateType || !this.newDateMonth || !this.newDateDay) return false;
      if (this.newDateType === "custom" && !this.newDateLabel.trim()) return false;
      return true;
    },
    upcomingDate() {
      const dates = this.person.significantDates;
      if (!dates || !dates.length) return null;

      const now = new Date();
      const refYear = now.getFullYear();
      const refMonth = now.getMonth();
      const refDay = now.getDate();
      const refMidnight = new Date(refYear, refMonth, refDay);
      const msPerDay = 1000 * 60 * 60 * 24;

      let nearest = null;
      let nearestDays = Infinity;

      for (const sd of dates) {
        let target = new Date(refYear, sd.month - 1, sd.day);
        if (target.getTime() < refMidnight.getTime()) {
          target = new Date(refYear + 1, sd.month - 1, sd.day);
        }
        const days = Math.round((target.getTime() - refMidnight.getTime()) / msPerDay);
        if (days <= 14 && days < nearestDays) {
          nearest = sd;
          nearestDays = days;
        }
      }

      return nearest ? { ...nearest, daysUntil: nearestDays } : null;
    },
    upcomingDateText() {
      if (!this.upcomingDate) return "";
      const days = this.upcomingDate.daysUntil;
      if (days === 0) return "is today!";
      if (days === 1) return "is tomorrow";
      return `in ${days} days`;
    },
  },
  methods: {
    monthName,
    formatSignificantDate,
    timeAgo(timestamp) {
      if (!timestamp) return "";
      const now = Date.now();
      const diff = now - timestamp;
      const seconds = Math.floor(diff / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);
      const weeks = Math.floor(days / 7);
      const months = Math.floor(days / 30);

      if (months > 0) return `${months} month${months === 1 ? "" : "s"} ago`;
      if (weeks > 0) return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
      if (days > 0) return `${days} day${days === 1 ? "" : "s"} ago`;
      if (hours > 0) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
      if (minutes > 0) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
      return "just now";
    },
    resetForm() {
      this.newDateType = "";
      this.newDateMonth = 0;
      this.newDateDay = null;
      this.newDateYear = null;
      this.newDateLabel = "";
    },
    async addSignal() {
      const text = this.newSignalText.trim();
      if (!text) return;
      this.saving = true;
      try {
        // Keep the stream bounded: the brief only reads the recent tail anyway.
        const updatedSignals = [...(this.person.signals || []).slice(-19), { text, at: Date.now() }];
        await updateDoc(doc(db, "relationships", this.person.id), {
          signals: updatedSignals,
          updatedAt: Date.now(),
        });
        this.newSignalText = "";
        this.showSignalForm = false;
        this.$emit("updated");
      } catch {
        this.$toast.error("Failed to save.");
      } finally {
        this.saving = false;
      }
    },
    async addDate() {
      if (!this.canAddDate) return;
      this.saving = true;
      try {
        const labels = { birthday: "Birthday", anniversary: "Anniversary" };
        const label = this.newDateType === "custom" ? this.newDateLabel.trim() : labels[this.newDateType];
        const newDate = {
          type: this.newDateType,
          label,
          month: this.newDateMonth,
          day: this.newDateDay,
          year: this.newDateYear || null,
        };
        const updatedDates = [...(this.person.significantDates || []), newDate];
        await updateDoc(doc(db, "relationships", this.person.id), {
          significantDates: updatedDates,
          updatedAt: Date.now(),
        });
        this.resetForm();
        this.showDateForm = false;
        this.$emit("updated");
      } catch {
        this.$toast.error("Failed to add date.");
      } finally {
        this.saving = false;
      }
    },
    handleAddressUpdate(parsedAddress) {
      if (!parsedAddress) {
        this.editAddress = { ...this.editAddress, street: "", city: "", state: "", postalCode: "", country: "" };
        return;
      }
      this.editAddress = {
        ...this.editAddress,
        street: parsedAddress.street || "",
        city: parsedAddress.city || "",
        state: parsedAddress.state || "",
        postalCode: parsedAddress.postalCode || "",
        country: parsedAddress.country || "",
      };
    },
    async saveAddress() {
      this.saving = true;
      try {
        await updateDoc(doc(db, "relationships", this.person.id), {
          address: { ...this.editAddress },
          hasAddress: true,
          updatedAt: Date.now(),
        });
        this.showAddressForm = false;
        this.$emit("updated");
      } catch {
        this.$toast.error("Failed to save address.");
      } finally {
        this.saving = false;
      }
    },
    async removeDate(index) {
      this.saving = true;
      try {
        const updatedDates = [...(this.person.significantDates || [])];
        updatedDates.splice(index, 1);
        await updateDoc(doc(db, "relationships", this.person.id), {
          significantDates: updatedDates,
          updatedAt: Date.now(),
        });
        this.$emit("updated");
      } catch {
        this.$toast.error("Failed to remove date.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.person-link { color: inherit; text-decoration: none; }
.person-link:hover { text-decoration: underline; }
.person-card {
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: box-shadow 0.3s ease;
}

.person-card:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}

.person-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.person-name {
  margin: 0;
  font-size: 1.2rem;
  color: var(--rich-black);
}

.type-badge {
  background-color: var(--powder-blue);
  color: var(--rich-black);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}

.gift-alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: linear-gradient(135deg, #ffe6ed, #ede7ff);
  border: 1px solid #ffd0de;
  color: var(--rich-black);
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  transition: box-shadow 0.2s, transform 0.1s;
}

.gift-alert:hover {
  box-shadow: 0 4px 12px rgba(214, 51, 132, 0.18);
  transform: translateY(-1px);
}

.gift-alert-cta {
  color: var(--hollywood-cerise);
  font-weight: 700;
  white-space: nowrap;
}

.vibe-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.vibe-chip {
  background-color: #ffe6ed;
  color: var(--hollywood-cerise);
  padding: 3px 10px;
  border-radius: 14px;
  font-size: 0.7rem;
  font-weight: 500;
}

.upcoming-date {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--accent2-color);
  padding: 2px 0;
}

.person-stats {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat {
  margin: 0;
  font-size: 0.85rem;
  color: var(--violet-lush);
}

.latest-signal {
  margin: 0.35rem 0 0;
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.signal-when {
  font-style: normal;
  opacity: 0.7;
}

.signal-hint {
  margin: 0.35rem 0;
  font-size: 0.78rem;
  color: var(--violet-lush);
  opacity: 0.85;
}

.last-sent {
  font-style: italic;
}

.agent-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 8px;
  margin-top: 2px;
}

.agent-status.on {
  background-color: #e6f7ec;
  color: #1e7a44;
}

.agent-status.warn {
  background-color: #fff4e0;
  color: #9a6700;
}

.agent-status.off {
  background-color: #f2f2f2;
  color: #888;
}

.agent-status-icon {
  font-size: 0.9rem;
  line-height: 1;
}

.person-actions {
  display: flex;
  gap: 10px;
  margin-top: 4px;
}

.person-actions .button {
  flex: 1;
  padding: 8px 15px;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.3s;
}

.person-actions .button.primary {
  background-color: var(--accent2-color);
  color: white;
}

.person-actions .button.primary:hover {
  background-color: var(--accent-color);
}

.person-actions .button.secondary {
  background-color: var(--hollywood-cerise);
  color: white;
}

.person-actions .button.secondary:hover {
  background-color: #e41393;
}

.saved-address {
  font-size: 0.8rem;
  color: var(--violet-lush);
  line-height: 1.4;
}

.address-text {
  font-style: italic;
}

.inline-address-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.inline-address-form .field-group {
  margin: 0;
}

.add-date-link {
  font-size: 0.8rem;
  color: var(--hollywood-cerise);
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
}

.add-date-link.signal-nudge {
  font-weight: 700;
}

.add-date-link:hover {
  text-decoration: underline;
}

.inline-date-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.existing-dates {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.date-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: #ffe6ed;
  color: var(--hollywood-cerise);
  padding: 3px 10px;
  border-radius: 14px;
  font-size: 0.75rem;
}

.remove-btn {
  background: none;
  border: none;
  color: var(--hollywood-cerise);
  font-size: 1rem;
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
}

.date-add-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.date-input {
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.8rem;
  box-sizing: border-box;
}

.date-input:focus {
  outline: none;
  border-color: var(--hollywood-cerise);
}

.date-select {
  width: auto;
  min-width: 80px;
}

.date-label-input {
  flex: 1 1 80px;
  min-width: 80px;
}

.date-day-input {
  width: 55px;
  flex: 0 0 55px;
}

.date-year-input {
  width: 65px;
  flex: 0 0 65px;
}

.date-form-actions {
  display: flex;
  gap: 6px;
}

.button.small {
  padding: 5px 12px;
  font-size: 0.8rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.button.small.primary {
  background-color: var(--accent2-color);
  color: white;
}

.button.small.primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button.small.secondary {
  background-color: var(--hollywood-cerise);
  color: white;
}

@media (max-width: 768px) {
  .person-card {
    padding: 16px;
  }
}
</style>
